"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { createContext, useContext, useId, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight, Check, ChevronDown, Info, Search, ShieldAlert, TriangleAlert } from "lucide-react";
import { ActionButton } from "@/features/home-v2/form/Action";
import { Spinner } from "@/features/home-v2/ui";
import { sanitizeUrl } from "@/lib/helpers";
import { autoFocusOnDesktop } from "@/lib/touch-focus";

/*
 * THE MONEY DIALOGS' KIT — deposit, withdraw and swap share one grammar: a
 * gilt step ruler that says where you are, rows that name a choice and open a
 * list in place (a native <dialog> is the top layer, so a page popover cannot
 * open over it — and on a phone a full list beats a dropdown), one amount
 * card, notices by tone, and a footer that states why it is blocked.
 */

/** Where the reader is: a diamond per step on one hairline, the passed ones gilt. */
export function Steps(t0) {
  const $ = _c(8);
  const {
    steps,
    at,
    label
  } = t0;
  let t1;
  if ($[0] !== at || $[1] !== steps) {
    let t2;
    if ($[3] !== at) {
      t2 = (s, i) => <li key={s} data-state={i < at ? "done" : i === at ? "now" : "next"} aria-current={i === at ? "step" : undefined}><span className="md-steps-mark" aria-hidden={true}>{i < at ? <Check /> : null}</span><span className="md-steps-label">{s}</span></li>;
      $[3] = at;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = steps.map(t2);
    $[0] = at;
    $[1] = steps;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== label || $[6] !== t1) {
    t2 = <ol className="md-steps" aria-label={label}>{t1}</ol>;
    $[5] = label;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/** A round coin: a token's or a network's image, gilt-rimmed. */
export function Coin(t0) {
  const $ = _c(10);
  const {
    src,
    size: t1
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  let t2;
  if ($[0] !== src) {
    t2 = src ? sanitizeUrl(src) : "";
    $[0] = src;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const safe = t2;
  let t3;
  if ($[2] !== size) {
    t3 = {
      inlineSize: size,
      blockSize: size
    };
    $[2] = size;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== safe || $[5] !== size) {
    t4 = safe ? <Image src={safe} alt="" fill={true} sizes={`${size}px`} /> : null;
    $[4] = safe;
    $[5] = size;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <span className="md-coin" style={t3} aria-hidden={true}>{t4}</span>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}

/** A chosen thing, named, that opens its list. */
export function PickRow(t0) {
  const $ = _c(27);
  const {
    label,
    labelEnd,
    icon,
    title,
    sub,
    end,
    onClick,
    disabled,
    busy
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="md-label">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== labelEnd || $[3] !== t1) {
    t2 = <div className="md-field-head">{t1}{labelEnd}</div>;
    $[2] = labelEnd;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = disabled || busy;
  let t4;
  if ($[5] !== busy || $[6] !== icon) {
    t4 = busy ? <span className="md-coin md-coin--wait" aria-hidden={true}><Spinner /></span> : icon;
    $[5] = busy;
    $[6] = icon;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== title) {
    t5 = <b>{title}</b>;
    $[8] = title;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== sub) {
    t6 = sub ? <small>{sub}</small> : null;
    $[10] = sub;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <span className="md-pick-copy">{t5}{t6}</span>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== end) {
    t8 = end ? <span className="md-pick-end">{end}</span> : null;
    $[15] = end;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronDown className="md-pick-chev" aria-hidden={true} />;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== onClick || $[19] !== t3 || $[20] !== t4 || $[21] !== t7 || $[22] !== t8) {
    t10 = <button type="button" className="md-pick v2-focus" onClick={onClick} disabled={t3} aria-haspopup="listbox">{t4}{t7}{t8}{t9}</button>;
    $[18] = onClick;
    $[19] = t3;
    $[20] = t4;
    $[21] = t7;
    $[22] = t8;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t2) {
    t11 = <div className="md-field">{t2}{t10}</div>;
    $[24] = t10;
    $[25] = t2;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}
export type PickItem = {
  key: string;
  icon?: ReactNode;
  title: string;
  sub?: ReactNode;
  end?: ReactNode;
  search?: string;
};

/**
 * A list that takes the dialog's body for a moment: back, a title, an
 * optional search and filter row, and the items — the chosen one with the
 * gold tick and spine, as in the sort menu. Choosing returns.
 */
export function PickerView(t0) {
  const $ = _c(30);
  const {
    title,
    items,
    value,
    onPick,
    onBack,
    backLabel,
    searchLabel,
    filters,
    empty,
    loading,
    loadingLabel,
    isFa
  } = t0;
  const [q, setQ] = useState("");
  let t1;
  if ($[0] !== items || $[1] !== q) {
    t1 = q.trim() ? items.filter(it => (it.search ?? it.title).toLowerCase().includes(q.trim().toLowerCase())) : items;
    $[0] = items;
    $[1] = q;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shown = t1;
  const Back = isFa ? ArrowRight : ArrowLeft;
  let t2;
  if ($[3] !== Back) {
    t2 = <Back aria-hidden={true} />;
    $[3] = Back;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== backLabel || $[6] !== onBack || $[7] !== t2) {
    t3 = <button type="button" className="md-back v2-focus" onClick={onBack}>{t2}{backLabel}</button>;
    $[5] = backLabel;
    $[6] = onBack;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== title) {
    t4 = <h3>{title}</h3>;
    $[9] = title;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="md-picker-head">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== q || $[15] !== searchLabel) {
    t6 = searchLabel ? <label className="md-search"><Search aria-hidden={true} /><input type="search" ref={autoFocusOnDesktop} autoComplete="off" spellCheck={false} placeholder={searchLabel} aria-label={searchLabel} value={q} onChange={e => setQ(e.target.value)} /></label> : null;
    $[14] = q;
    $[15] = searchLabel;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== empty || $[18] !== loading || $[19] !== loadingLabel || $[20] !== onPick || $[21] !== shown || $[22] !== title || $[23] !== value) {
    t7 = loading ? <p className="md-picker-empty"><Spinner />{loadingLabel}</p> : shown.length === 0 ? <p className="md-picker-empty">{empty}</p> : <ul className="md-picker-list" role="listbox" aria-label={title}>{shown.map(it_0 => <li key={it_0.key}><button type="button" role="option" aria-selected={it_0.key === value} className="md-option v2-focus" onClick={() => onPick(it_0.key)}>{it_0.icon}<span className="md-pick-copy"><b>{it_0.title}</b>{it_0.sub ? <small>{it_0.sub}</small> : null}</span>{it_0.end ? <span className="md-option-end">{it_0.end}</span> : null}{it_0.key === value ? <Check className="md-option-tick" aria-hidden={true} /> : null}</button></li>)}</ul>;
    $[17] = empty;
    $[18] = loading;
    $[19] = loadingLabel;
    $[20] = onPick;
    $[21] = shown;
    $[22] = title;
    $[23] = value;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== filters || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t8 = <div className="md-picker">{t5}{t6}{filters}{t7}</div>;
    $[25] = filters;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}

/** THE AMOUNT: one card — its label and what is available above, the figure large, its token beside it, the verdict under. */
export function AmountCard(t0) {
  const $ = _c(25);
  const {
    label,
    value,
    onChange,
    top,
    side,
    message,
    tone,
    readOnly,
    inputId
  } = t0;
  const auto = useId();
  const id = inputId ?? auto;
  const msgId = `${id}-msg`;
  const t1 = tone === "error" ? "error" : undefined;
  let t2;
  if ($[0] !== id || $[1] !== label) {
    t2 = <label htmlFor={id} className="md-label">{label}</label>;
    $[0] = id;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2 || $[4] !== top) {
    t3 = <div className="md-amount-top">{t2}{top}</div>;
    $[3] = t2;
    $[4] = top;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== id || $[7] !== message || $[8] !== msgId || $[9] !== onChange || $[10] !== readOnly || $[11] !== tone || $[12] !== value) {
    t4 = readOnly ? <output id={id} className="md-amount-input" dir="ltr">{value || "\u2014"}</output> : <input id={id} className="md-amount-input" type="text" inputMode="decimal" autoComplete="off" dir="ltr" placeholder="0.00" value={value} onChange={e => onChange?.(e.target.value)} aria-invalid={tone === "error" || undefined} aria-describedby={message ? msgId : undefined} />;
    $[6] = id;
    $[7] = message;
    $[8] = msgId;
    $[9] = onChange;
    $[10] = readOnly;
    $[11] = tone;
    $[12] = value;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== side || $[15] !== t4) {
    t5 = <div className="md-amount-row">{t4}{side}</div>;
    $[14] = side;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== message || $[18] !== msgId) {
    t6 = message ? <p id={msgId} className="md-amount-msg">{message}</p> : null;
    $[17] = message;
    $[18] = msgId;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t1 || $[21] !== t3 || $[22] !== t5 || $[23] !== t6) {
    t7 = <div className="md-amount" data-tone={t1}>{t3}{t5}{t6}</div>;
    $[20] = t1;
    $[21] = t3;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}

/** A token chip inside an amount card — its coin and ticker; a button when it opens the list. */
export function TokenChip(t0) {
  const $ = _c(15);
  const {
    image,
    symbol,
    placeholder,
    onClick,
    label
  } = t0;
  let t1;
  if ($[0] !== image) {
    t1 = <Coin src={image} size={24} />;
    $[0] = image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = symbol || placeholder;
  let t3;
  if ($[2] !== t2) {
    t3 = <b>{t2}</b>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClick) {
    t4 = onClick ? <ChevronDown aria-hidden={true} /> : null;
    $[4] = onClick;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t3 || $[8] !== t4) {
    t5 = <>{t1}{t3}{t4}</>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const body = t5;
  let t6;
  if ($[10] !== body || $[11] !== label || $[12] !== onClick || $[13] !== symbol) {
    t6 = onClick ? <button type="button" className="md-chip v2-focus" onClick={onClick} aria-label={label} data-empty={!symbol || undefined}>{body}</button> : <span className="md-chip" data-static={true}>{body}</span>;
    $[10] = body;
    $[11] = label;
    $[12] = onClick;
    $[13] = symbol;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

/** A notice: a warning (irreversible), an alert, or a plain note. */
export function Note(t0) {
  const $ = _c(8);
  const {
    tone: t1,
    children
  } = t0;
  const tone = t1 === undefined ? "info" : t1;
  const Icon = tone === "warn" ? ShieldAlert : tone === "alert" ? TriangleAlert : Info;
  let t2;
  if ($[0] !== Icon) {
    t2 = <Icon aria-hidden={true} />;
    $[0] = Icon;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <span>{children}</span>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== tone) {
    t4 = <p className="md-note" data-tone={tone}>{t2}{t3}</p>;
    $[4] = t2;
    $[5] = t3;
    $[6] = tone;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}

/** A request from this footer is out: its other buttons (back, cancel) hold still until it answers. */
const FootBusy = createContext(false);

/** The footer: why it is blocked (said, not implied by a grey button), then the buttons. */
export function Foot(t0) {
  const $ = _c(11);
  const {
    reason,
    busy: t1,
    children
  } = t0;
  const busy = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== busy || $[1] !== reason) {
    t2 = reason && !busy ? <p className="md-foot-why" role="status">{reason}</p> : null;
    $[0] = busy;
    $[1] = reason;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children) {
    t3 = <div className="md-foot-acts">{children}</div>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== busy || $[6] !== t3) {
    t4 = <FootBusy.Provider value={busy}>{t3}</FootBusy.Provider>;
    $[5] = busy;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="md-foot">{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/**
 * A footer button. The primary one is the shared action (features/home-v2/form/Action): the
 * spinner and the waiting label in the label's place at the same width, every press swallowed
 * while it works. A ghost one (back, cancel) is off while the footer's request is out.
 */
export function Act(t0) {
  const $ = _c(14);
  const {
    children,
    onClick,
    disabled,
    busy,
    busyLabel,
    ghost,
    ariaLabel
  } = t0;
  const footBusy = useContext(FootBusy);
  if (ghost) {
    const t1 = disabled || busy || footBusy;
    let t2;
    if ($[0] !== ariaLabel || $[1] !== children || $[2] !== onClick || $[3] !== t1) {
      t2 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onClick} disabled={t1} aria-label={ariaLabel}>{children}</button>;
      $[0] = ariaLabel;
      $[1] = children;
      $[2] = onClick;
      $[3] = t1;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t1;
  if ($[5] !== onClick) {
    t1 = () => onClick();
    $[5] = onClick;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== ariaLabel || $[8] !== busy || $[9] !== busyLabel || $[10] !== children || $[11] !== disabled || $[12] !== t1) {
    t2 = <ActionButton chain={true} className="md-primary" onClick={t1} disabled={disabled} busy={busy} busyLabel={busyLabel} ariaLabel={ariaLabel}>{children}</ActionButton>;
    $[7] = ariaLabel;
    $[8] = busy;
    $[9] = busyLabel;
    $[10] = children;
    $[11] = disabled;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
}